import React, { useContext, useRef, useState } from 'react';
import type { FormApi } from 'final-form';
import { Helmet as MetaTags } from 'react-helmet';
import {
  navigate,
  RouteComponentProps,
  useLocation,
  WindowLocation,
} from '@gatsbyjs/reach-router';
import { AuthContext } from '~/Root/Auth';
import { Auth } from '~/amplify';
import { Heading1 } from '~/components/atom/Typography';
import AuthFormWrapper from '../components/AuthFormWrapper';
import UnauthNav from '~/components/organism/UnauthNav';
import createPageTitle from '~/util/createPageTitle';
import { AUTH_PATHS } from '~/components/page/Auth/constants';
import { Body } from '~/components/atom/Typography';
import { Form, Field } from '~/components/organism/Forms';
import OTPInput from '~/components/molecule/OTPInput';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Button from '~/components/atom/Button';
import TextButton from '~/components/atom/TextButton';
import { reporter } from '~/hooks/useErrorReporter';
import { isExpiredOrNotAuthorizedSessionError } from '~/Errors';
import styled, { css } from 'styled-components';
import Catalog from '~/Catalog';
import useDHFlag from '~/hooks/useDHFlag';
import { SIGN_IN_STEP, type SignInStep } from '~/amplify/constants';
import useIntercom from '~/hooks/useIntercom';
import TEST_ID from './index.testid';

const text = {
  title: 'Tweestaps\u00ADverificatie',
  description: 'Voer de code in vanuit je authenticator-app',
  submitButton: 'Inloggen',
  errorMessage: 'Ongeldige code. Probeer het opnieuw.',
  lostDevice: 'Ik ben mijn apparaat kwijt',
  codeLengthError: 'Voer de volledige 6-cijferige code in.',
  needHelp: 'Ik heb hulp nodig',
  needHelpIntercomMessage:
    'Ik ben mijn authenticator apparaat kwijt en heb geen backup telefoonnummer.',
};

const CODE_LENGTH = 6;

const validate = ({ code }: { code: string }) => {
  if (!code || code.length !== CODE_LENGTH) {
    return { code: text.codeLengthError };
  }
  return {};
};

type WindowState = {
  from?: string;
  signInStep?: SignInStep;
};

const MFA: React.FC<RouteComponentProps> = () => {
  const { ensureLogin } = useContext(AuthContext);
  const intercom = useIntercom();
  const location = useLocation() as WindowLocation<WindowState>;
  const isMfaEnabled = useDHFlag('mfa');

  const from = location.state?.from;
  /**
   * If the signInStep is CONFIRM_SIGN_IN_WITH_TOTP_CODE, then we need to skip the selection step
   * If the signInStep is CONTINUE_SIGN_IN_WITH_MFA_SELECTION, then we need to show the selection step
   *
   * Refreshing the page or going to the /mfa route directly lands on login page, so we do not need to worry about that.
   */
  const needsSelection =
    location.state?.signInStep !== SIGN_IN_STEP.CONFIRM_SIGN_IN_WITH_TOTP_CODE;

  const onSignInSuccess = () => {
    ensureLogin();
    void navigate(from ?? '/-/', { replace: true });
  };

  const [totpError, setTotpError] = useState<string | null>(null);
  const totpSelectedRef = useRef(false);

  if (!isMfaEnabled) return <>MFA feature flag is not enabled</>;

  const onSubmit = async (
    { code }: { code: string },
    form: FormApi<{ code: string }>,
  ) => {
    setTotpError(null);

    try {
      // Step 1: Select TOTP as the MFA method. Only needed the first time —
      // on retry after a bad code Cognito stays on CONFIRM_SIGN_IN_WITH_TOTP_CODE,
      // and re-selecting would throw.
      if (needsSelection && !totpSelectedRef.current) {
        const selectionResult = await Auth.confirmSignIn({
          challengeResponse: 'TOTP',
        });

        totpSelectedRef.current = true;

        if (selectionResult.isSignedIn) {
          // noop, we have not entered the code yet.
          reporter.captureMessage(
            `TotpView: User is signed in but has not entered the code yet.`,
            'warning',
          );
          void navigate('/-/');
          return;
        }
      }

      // Step 2: Submit the actual TOTP code
      const signInResult = await Auth.confirmSignIn({
        challengeResponse: code,
      });

      if (signInResult.isSignedIn) {
        onSignInSuccess();
        return;
      }

      reporter.captureMessage(
        `MFA verification incomplete: ${signInResult.nextStep.signInStep}`,
        'warning',
      );
      setTotpError(text.errorMessage);
      form.reset({ code: '' });
    } catch (err) {
      if (isExpiredOrNotAuthorizedSessionError(err)) {
        void navigate(AUTH_PATHS.LOGIN, {
          replace: true,
          state: { from, message: Catalog.sessionExpired },
        });
        return;
      }

      reporter.captureMessage(`MFA: Failed to confirmSignIn: ${err}`, 'error');
      setTotpError(text.errorMessage);
      form.reset({ code: '' });
    }
  };

  return (
    <>
      <MetaTags>
        <title>{createPageTitle(text.title)}</title>
      </MetaTags>
      <UnauthNav
        buttons={[
          {
            icon: 'sign-out',
            onClick: () => navigate(AUTH_PATHS.LOGOUT),
            ghost: true,
          },
        ]}
      />
      <AuthFormWrapper>
        <Heading1
          lineHeight="base"
          margin={[null, null, 'l']}
          color={{ group: 'accent' }}
          size="xxxl"
          padding={[null, null, null, 'xxxl']}
          skewed
        >
          {text.title}
        </Heading1>

        <Form
          initialValues={{ code: '' }}
          onSubmit={onSubmit}
          validate={validate}
        >
          {({ handleSubmit, submitting, invalid, form }) => (
            <StyledForm onSubmit={handleSubmit}>
              <JustificationContainer
                align="center"
                margin={['xl', null]}
                direction="column"
                gap="l"
              >
                <Body margin={[null]} color={{ group: 'white' }} size="base">
                  {text.description}
                </Body>

                {totpError && (
                  <Body
                    margin={[null]}
                    color={{ group: 'danger', variant: 'light' }}
                    size="base"
                    dataTestId={TEST_ID.ERROR_MESSAGE}
                  >
                    {totpError}
                  </Body>
                )}

                <Field name="code">
                  {({ input }) => (
                    <OTPInput
                      length={CODE_LENGTH}
                      value={input.value ?? ''}
                      onChange={input.onChange}
                      onBlur={input.onBlur}
                      disabled={submitting}
                      autoFocus
                      onComplete={() => {
                        void form.submit();
                      }}
                    />
                  )}
                </Field>
              </JustificationContainer>

              <JustificationContainer
                align="center"
                justification="space-between"
                gap="base"
                width="100%"
              >
                <TextButton
                  label={needsSelection ? text.lostDevice : text.needHelp}
                  dataTestId={
                    needsSelection
                      ? TEST_ID.LOST_DEVICE_BUTTON
                      : TEST_ID.NEED_HELP_BUTTON
                  }
                  onClick={() => {
                    if (needsSelection) {
                      void navigate(AUTH_PATHS.ACCOUNT_RECOVERY, {
                        state: { from },
                      });
                    } else {
                      intercom.openChat(text.needHelp);
                    }
                  }}
                  appearance="accent"
                  size="medium"
                />
                <Button
                  appearance="primary"
                  size="medium"
                  type="submit"
                  disabled={submitting || invalid}
                  loading={submitting}
                  label={text.submitButton}
                />
              </JustificationContainer>
            </StyledForm>
          )}
        </Form>
      </AuthFormWrapper>
    </>
  );
};

const StyledForm = styled.form(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.space('base')};
  `,
);

export default MFA;
